import {
  useCallback,
  useMemo,
  memo,
  FocusEvent,
  ReactNode,
  ChangeEvent
} from 'react'

import { observer } from 'mobx-react'
import { UseFieldConfig } from 'react-final-form'

import { TextFieldProps, useMediaQuery, useTheme } from '@mui/material'

import {
  useText,
  TextFormatter,
  textFormat
} from '@finanzcheck/catalyst-renderless-components'

import { useStores } from 'stores/utils/hooks/useStores'
import { Debtor } from 'containers/PersonalForm/PersonalForm.interface'
import { usePatchedField } from 'stores/utils/hooks/usePatchedField'
import Text from 'components/Input/Text'
import FieldDataCollector from 'components/FieldDataCollector'
import { useMeta } from 'stores/utils/hooks/useMeta'
import { PathFormFieldProps } from 'interfaces/PathFormComponents.interface'
import WithFieldInteraction from 'utils/tracking/amplitude/WithFieldInteraction'
import WithIdleFieldChange from 'components/WithIdleFieldChange'
import { isInIframeContext } from 'stores/RootStore'
import { scrollTo } from 'utils/pollard/scroll'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'
import { progressiveDisclosureValidator, VALIDATOR_EMAIL } from 'validation'

interface OwnProps {
  acceptedInput?: RegExp
  debtor?: Debtor
  maxLength?: number
  clue?: string
  disabled?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldConfig?: UseFieldConfig<any>
  formatters?: Array<TextFormatter>
  inputProps?: TextFieldProps['inputProps']
  endAdornment?: ReactNode
  trackOnChange?: boolean
  delayInMs?: number
  disablePatchField?: boolean
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
  outerLabel?: boolean
  smallTextField?: boolean
  showTooltipOnFieldClick?: boolean
  isTooltipHidden?: boolean
  scrollOnFocusEnabled?: boolean
}

type Props = PathFormFieldProps & OwnProps

const TextInput = observer(
  ({
    path,
    onChange,
    onChangeConstraints,
    onBlur,
    onFocus,
    onKeyDown,
    clue,
    onTooltip,
    maxLength,
    fieldConfig = {},
    formatters = [],
    inputProps,
    delayInMs,
    endAdornment,
    outerLabel,
    smallTextField,
    showTooltipOnFieldClick,
    isTooltipHidden,
    disablePatchField,
    scrollOnFocusEnabled,
    autoComplete,
    acceptedInput,
    disabled
  }: Props) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    /* because for smava we want to only create customer on CTA click so that we can validate CSO
    :( not happy while adding this code, should be handeld in BE ideally {disablePatchField} */

    const { input, error } = usePatchedField(
      path,
      fieldConfig,
      !disablePatchField
    )
    const { tooltip, label, placeholder } = useMeta(path)

    const {
      traversal: { data, removeErrorsForPath },
      navigation: { tenantContext }
    } = useStores()

    const changeHandler = useCallback(
      (value: string, event: ChangeEvent<HTMLElement>) => {
        if (
          onChangeConstraints?.minLength &&
          value.length < onChangeConstraints.minLength
        ) {
          return
        }

        input.onChange(value)

        if (
          onChangeConstraints &&
          !onChangeConstraints.doNotInvokeBlurOnChange
        ) {
          input.onBlur()
        }

        if (onChange) {
          onChange(value)
        }

        if (
          isMobile &&
          disablePatchField &&
          progressiveDisclosureValidator(VALIDATOR_EMAIL)(value) &&
          isNotAffiliate(data, tenantContext)
        ) {
          const scrollTop =
            window.scrollY ||
            window.pageYOffset ||
            document.body.scrollTop +
              ((document.documentElement &&
                document.documentElement.scrollTop) ||
                0)

          const pos = event.target.getBoundingClientRect().top + scrollTop
          scrollTo(pos, isInIframeContext())
        }

        /* 
          this if statement prevents removing errors when adding a space 
          in the both ends of the string in the field with error
        */
        if (value === value.trim()) {
          removeErrorsForPath(path)
        }
      },
      [
        onChangeConstraints,
        input,
        onChange,
        disablePatchField,
        removeErrorsForPath,
        path,
        isMobile,
        data,
        tenantContext
      ]
    )
    const blurHandler = useCallback(
      (value: string, event: FocusEvent<HTMLElement>) => {
        const formattedValue = textFormat(value.trim(), formatters)

        input.onChange(formattedValue)
        input.onBlur(event)

        if (onBlur) {
          onBlur(formattedValue)
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [input, onBlur]
    )
    const focusHandler = useCallback(
      (value: string, event: FocusEvent<HTMLElement>) => {
        input.onFocus(event)

        if (onFocus) {
          onFocus(value)
        }
      },
      [input, onFocus]
    )

    const textInputProps = useMemo(
      () => ({ maxLength, ...inputProps }),
      [inputProps, maxLength]
    )

    const { ref, getProps } = useText({
      value: input.value || '',
      formatters: []
    })

    const { id, name, type } = getProps({ name: path, id: path })

    const inputElement = (
      <Text
        tooltip={tooltip}
        label={label}
        placeholder={placeholder}
        autoComplete={autoComplete}
        acceptedInput={acceptedInput}
        id={id}
        name={name || input.name}
        type={type}
        path={path}
        onKeyDown={onKeyDown}
        ref={ref}
        onChange={changeHandler}
        onBlur={blurHandler}
        onFocus={focusHandler}
        error={error}
        clue={clue}
        onTooltip={onTooltip}
        inputProps={textInputProps}
        endAdornment={endAdornment}
        outerLabel={outerLabel}
        smallTextField={smallTextField}
        showTooltipOnFieldClick={showTooltipOnFieldClick}
        isTooltipHidden={isTooltipHidden}
        scrollOnFocusEnabled={scrollOnFocusEnabled}
        disabled={disabled}
      />
    )

    return (
      <WithIdleFieldChange
        {...((delayInMs || delayInMs === 0) && { delayInMs })}
      >
        {inputElement}
      </WithIdleFieldChange>
    )
  }
)

const PathTextInput = (props: Props) => {
  const {
    path,
    labelPath,
    trackOnChange,
    autoComplete,
    acceptedInput,
    delayInMs,
    isTooltipHidden,
    outerLabel,
    scrollOnFocusEnabled,
    showTooltipOnFieldClick,
    smallTextField,
    debtor,
    maxLength,
    clue,
    disabled,
    fieldConfig,
    formatters,
    inputProps,
    endAdornment,
    disablePatchField,
    onKeyDown,
    type,
    onChange,
    onBlur,
    onChangeConstraints
  } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <WithFieldInteraction path={path} trackOnChange={trackOnChange}>
        <TextInput
          path={path}
          debtor={debtor}
          acceptedInput={acceptedInput}
          autoComplete={autoComplete}
          delayInMs={delayInMs}
          isTooltipHidden={isTooltipHidden}
          outerLabel={outerLabel}
          scrollOnFocusEnabled={scrollOnFocusEnabled}
          showTooltipOnFieldClick={showTooltipOnFieldClick}
          smallTextField={smallTextField}
          maxLength={maxLength}
          clue={clue}
          disabled={disabled}
          fieldConfig={fieldConfig}
          formatters={formatters}
          inputProps={inputProps}
          endAdornment={endAdornment}
          disablePatchField={disablePatchField}
          onKeyDown={onKeyDown}
          type={type}
          onChange={onChange}
          onBlur={onBlur}
          onChangeConstraints={onChangeConstraints}
        />
      </WithFieldInteraction>
    </FieldDataCollector>
  )
}

export default memo(PathTextInput)
